import * as LabelPrimitive from '@radix-ui/react-label'
import type { LucideIcon } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/** The deck's input treatment (slide 8): pale fill, hairline border. */
export const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<'input'>>(
  ({ className, ...props }, ref) => (
    <input
      ref={ref}
      className={cn(
        'w-full min-h-11 rounded-xl border border-line bg-surface px-3.5 py-2.5 text-ink',
        'placeholder:text-muted-3 transition-colors',
        'focus:bg-white focus:border-teal focus:outline-none focus:ring-3 focus:ring-teal/15',
        'aria-invalid:border-orange aria-invalid:bg-orange-soft/40',
        className,
      )}
      {...props}
    />
  ),
)
Input.displayName = 'Input'

/**
 * An input with a leading icon. The icon is decorative — the field's label
 * carries the meaning — so it is hidden from assistive technology.
 */
export const InputWithIcon = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<'input'> & { icon: LucideIcon }
>(({ icon: Icon, className, ...props }, ref) => (
  <div className="relative">
    <Icon
      className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-3"
      strokeWidth={2}
      aria-hidden
    />
    <Input ref={ref} className={cn('pl-9', className)} {...props} />
  </div>
))
InputWithIcon.displayName = 'InputWithIcon'

export const Label = React.forwardRef<
  React.ComponentRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn('block text-[12.5px] font-bold text-ink mb-1', className)}
    {...props}
  />
))
Label.displayName = 'Label'

/** Label + control + error, the shape every form row uses. */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  className,
  children,
}: {
  label: string
  htmlFor?: string
  hint?: React.ReactNode
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('min-w-0', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="mt-1 text-[11.5px] text-orange">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[11.5px] text-muted">{hint}</p>
      ) : null}
    </div>
  )
}
